import React from 'react';
    import { motion } from 'framer-motion';
    import * as FiIcons from 'react-icons/fi';
    import SafeIcon from '../common/SafeIcon';
    import Navigation from '../components/Navigation';
    import CosmicBackground from '../components/CosmicBackground';

    const { FiSettings, FiZap, FiTrendingUp, FiShield, FiArrowRight, FiCheck, FiStar, FiUsers, FiBox, FiMic, FiMail, FiSearch, FiLogIn, FiPenTool } = FiIcons;

    const LumadiousPage = () => {
      const enterpriseFeatures = [
        { icon: FiZap, title: 'Manage 3 Income Streams', description: 'Run up to three distinct business models or brands from a single,unified login.' },
        { icon: FiSettings, title: 'Themed Ecosystems', description: 'Each income stream gets its own themed page and ecosystem,maintaining brand separation.' },
        { icon: FiTrendingUp, title: 'Advanced Analytics Suite', description: 'Deep insights into every aspect of your business with predictive analytics.' },
        { icon: FiShield, title: 'Enterprise-Grade Security', description: 'Bank-level security with compliance features for serious businesses.' },
      ];

      const growthPathways = [
        { icon: FiZap, title: 'SaaS Builder', description: 'Launch your own Software-as-a-Service product.' },
        { icon: FiUsers, title: 'Community Builder', description: 'Create and manage a thriving online community.' },
        { icon: FiPenTool, title: 'Brand Bible (Marketing)', description: 'Develop and automate your brand marketing strategy.' },
        { icon: FiBox, title: 'Content Creation Automations', description: 'Automate your content pipeline from idea to distribution.' },
        { icon: FiMail, title: 'Email Marketing & SEO', description: 'Excel in your inbound marketing with powerful automation.' },
        { icon: FiLogIn, title: 'The "One Log-In" Business', description: 'Consolidate all your operations into a single platform.' },
      ];

      const onboardingProcess = [
        { step: '1', title: 'Discovery & Strategy', description: 'Deep dive consultation to map your current business and automation goals', duration: 'Week 1-2' },
        { step: '2', title: 'System Architecture', description: 'Custom workflow design and system integration planning', duration: 'Week 3-4' },
        { step: '3', title: 'Implementation', description: 'Complete automation setup with testing and optimization', duration: 'Week 5-6' },
        { step: '4', title: 'Training & Launch', description: 'Comprehensive team training and go-live support', duration: 'Week 7-8' },
      ];

      const testimonials = [
        { name: 'Marcus Chen', role: 'Digital Agency Owner', text: 'The 8-week onboarding was incredible. They mapped our entire business and built automations we never knew were possible. Lumadious saved me 40 hours per week.', revenue: '$180K/month', rating: 5 },
        { name: 'Elena Rodriguez', role: 'Course Creator', text: 'The team spent weeks understanding my business before building anything. From launch to delivery,everything is automated. I can focus on creating.', revenue: '$95K/month', rating: 5 },
      ];

      const handleBookingClick = () => {
        window.open('https://connect.withjahx.com', '_blank');
      };

      return (
        <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
          <CosmicBackground />
          <Navigation />

          {/* Hero Section */}
          <section className="pt-32 pb-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center max-w-4xl mx-auto"
              >
                <div className="inline-flex items-center space-x-2 bg-blue-900/30 px-4 py-2 rounded-full border border-blue-500/30 mb-6">
                  <SafeIcon icon={FiSettings} className="text-blue-400" />
                  <span className="text-sm font-poppins text-blue-200">Enterprise Automation</span>
                </div>
                <h1 className="text-5xl md:text-7xl font-orbitron font-bold mb-6 leading-tight">
                  <span className="clarity-gradient">Lumadious</span>
                </h1>
                <p className="text-xl md:text-2xl text-gray-300 mb-8 font-poppins font-light max-w-3xl mx-auto leading-relaxed">
                  The complete business automation platform for entrepreneurs ready to scale. Build sophisticated systems that run your business while you sleep.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-12">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleBookingClick}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 rounded-full font-poppins font-semibold text-lg glow-button flex items-center space-x-2"
                  >
                    <span>Schedule Discovery Call</span>
                    <SafeIcon icon={FiArrowRight} />
                  </motion.button>
                  <div className="text-center">
                    <p className="text-gray-400 font-poppins text-sm">Calls on Google Meet or Discord</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Growth Pathways Section ($55) */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Growth Pathways <span className="text-3xl text-purple-300 font-poppins font-light tracking-wide">($55/month)</span>
                </h2>
                <p className="text-xl text-gray-300 font-poppins max-w-3xl mx-auto">
                  Choose your own adventure. Specialize in a pathway to build and automate a specific part of your business.
                </p>
              </motion.div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {growthPathways.map((pathway, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                    className="bg-gradient-to-br from-purple-800/40 to-blue-800/40 p-6 rounded-2xl border border-purple-500/40 backdrop-blur-sm transition-all duration-300"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mb-6">
                      <SafeIcon icon={pathway.icon} className="text-white text-2xl" />
                    </div>
                    <h3 className="text-xl font-orbitron font-bold mb-4 text-white">{pathway.title}</h3>
                    <p className="text-gray-300 font-poppins text-sm leading-relaxed">{pathway.description}</p>
                  </motion.div>
                ))}
              </div>
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                viewport={{ once: true }}
                className="text-center bg-gray-800/30 p-6 rounded-2xl max-w-2xl mx-auto border border-gray-700"
              >
                <p className="font-poppins text-gray-300">
                  All Growth Pathways also include access to <strong className="text-purple-300">POD & Pop-up Shop</strong> systems to help physical and digital sellers manage their business.
                </p>
              </motion.div>
            </div>
          </section>

          {/* Enterprise Section ($88) */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Lumadious Enterprise <span className="text-3xl text-blue-300 font-poppins font-light tracking-wide">($88/month)</span>
                </h2>
                <p className="text-xl text-gray-300 font-poppins max-w-3xl mx-auto">
                  The ultimate all-in-one platform for serious entrepreneurs ready to automate and scale multiple income streams.
                </p>
              </motion.div>
              <div className="grid md:grid-cols-2 gap-8 mb-16">
                {enterpriseFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                    className="bg-gradient-to-br from-blue-800/50 to-purple-800/50 p-8 rounded-2xl border border-blue-500/50 backdrop-blur-sm hover:border-blue-400/70 transition-all duration-300"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mb-6">
                      <SafeIcon icon={feature.icon} className="text-white text-2xl" />
                    </div>
                    <h3 className="text-2xl font-orbitron font-bold mb-4 text-white">{feature.title}</h3>
                    <p className="text-gray-300 font-poppins leading-relaxed">{feature.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Onboarding Process */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Your <span className="clarity-gradient">Enterprise Onboarding</span>
                </h2>
                <p className="text-xl text-gray-300 font-poppins max-w-3xl mx-auto">
                  8 weeks of dedicated setup to transform your entire business operation (Included with Enterprise).
                </p>
              </motion.div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
                {onboardingProcess.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center bg-gradient-to-br from-blue-800/30 to-purple-800/30 p-6 rounded-2xl border border-blue-500/30"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-orbitron font-bold text-xl">{step.step}</span>
                    </div>
                    <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 px-3 py-1 rounded-full inline-block mb-4">
                      <span className="text-blue-300 font-poppins text-sm">{step.duration}</span>
                    </div>
                    <h3 className="text-xl font-orbitron font-bold mb-3 text-white">{step.title}</h3>
                    <p className="text-gray-300 font-poppins text-sm">{step.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center bg-gradient-to-r from-blue-900/40 to-purple-900/40 p-12 rounded-3xl border border-blue-500/30 backdrop-blur-sm max-w-4xl mx-auto"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Ready to <span className="clarity-gradient">Automate</span> Everything?
                </h2>
                <p className="text-xl text-gray-300 font-poppins mb-8 max-w-2xl mx-auto">
                  Let's discuss how our Growth and Enterprise plans can build a business that runs itself.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-center justify-center space-x-3 text-gray-300 font-poppins">
                    <SafeIcon icon={FiCheck} className="text-blue-400" />
                    <span>8-week enterprise onboarding included</span>
                  </div>
                  <div className="flex items-center justify-center space-x-3 text-gray-300 font-poppins">
                    <SafeIcon icon={FiCheck} className="text-blue-400" />
                    <span>Dedicated success manager</span>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBookingClick}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 px-12 py-4 rounded-full font-poppins font-semibold text-xl glow-button"
                >
                  Schedule Discovery Call
                </motion.button>
                <p className="text-gray-400 font-poppins text-sm mt-4">
                  Calls are held on Google Meet or Discord
                </p>
              </motion.div>
            </div>
          </section>
        </div>
      );
    };

    export default LumadiousPage;